import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cartUtils } from '@/data/translatedData';
import { useLanguage } from '@/context/LanguageContext';
import { t, tx } from '@/translations/translations';

const Header = ({ onCartClick, onNavigate }) => {
  const [cartCount, setCartCount] = useState(0);
  const [cartPulse, setCartPulse] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);

    const handleItemAdded = () => {
      setCartPulse(true);
      setTimeout(() => setCartPulse(false), 700);
    };
    window.addEventListener('cartItemAdded', handleItemAdded);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('cartItemAdded', handleItemAdded);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updateCartCount = () => {
    setCartCount(cartUtils.getItemCount());
  };

  const scrollToSection = (sectionId) => {
    // For hub sub-sections, dispatch event to open them and scroll to hub
    const hubSections = ['significado', 'halajot', 'material', 'meritos'];
    if (hubSections.includes(sectionId)) {
      window.dispatchEvent(new CustomEvent('openHubSection', { detail: sectionId }));
      const hub = document.getElementById('mas-del-tzitzit');
      if (hub) hub.scrollIntoView({ behavior: 'smooth' });
    } else {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setMobileSubOpen(false);
  };

  const subLinks = [
    { id: 'significado', es: 'Significado', en: 'Meaning', he: 'משמעות' },
    { id: 'halajot', es: 'Halajot', en: 'Halakhot', he: 'הלכות' },
    { id: 'material', es: 'Material', en: 'Material', he: 'חומרים' },
    { id: 'meritos', es: 'Lo que Dicen Nuestros Sabios', en: 'What Our Sages Say', he: 'דברי חכמינו' },
  ];

  return (
    <header dir="ltr" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-slate-950 shadow-xl' : 'bg-slate-950/95 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => scrollToSection('productos')}>
            <img 
              src="/products/mitzva-logo.webp" 
              alt="Mitzva Fit" 
              className="h-8 sm:h-10 w-auto invert"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('productos')}
              className="text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium"
            >
              {t(language, 'nav.products')}
            </button>

            <button
              onClick={() => scrollToSection('certificacion')}
              className="text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium"
            >
              {tx(language, 'Certificación', 'Certification', 'כשרות')}
            </button>
            <button
              onClick={() => scrollToSection('contacto')}
              className="text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium"
              data-testid="nav-contact"
            >
              {tx(language, 'Contacto', 'Contact', 'צור קשר')}
            </button>

            {/* Más del Tzitzit dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium"
                data-testid="nav-mas-tzitzit"
              >
                {tx(language, 'Más del Tzitzit', 'More about Tzitzit', 'עוד על הציצית')}
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-slate-900 border border-slate-700/60 rounded-xl shadow-2xl shadow-black/40 overflow-hidden">
                  {subLinks.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => scrollToSection(link.id)}
                      className="block w-full text-left px-5 py-3 text-sm text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors"
                      data-testid={`nav-sub-${link.id}`}
                    >
                      {tx(language, link.es, link.en, link.he)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-1 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-slate-300 hover:text-cyan-400 hover:bg-slate-800 h-8 px-2 sm:h-9 sm:px-3 text-xs sm:text-sm"
            >
              <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="ml-1 sm:ml-2" data-testid="language-toggle-label">{language === 'es' ? 'ES' : language === 'en' ? 'EN' : 'עברית'}</span>
            </Button>

            <Button
              variant="outline"
              size="icon"
              className={`relative border-slate-700 hover:bg-slate-800 h-8 w-8 sm:h-10 sm:w-10 transition-transform duration-300 ${cartPulse ? 'scale-125 ring-2 ring-cyan-400 ring-offset-2 ring-offset-slate-950' : ''}`}
              onClick={onCartClick}
              data-testid="cart-button"
            >
              <ShoppingCart className={`h-4 w-4 sm:h-5 sm:w-5 text-slate-300 ${cartPulse ? 'animate-bounce' : ''}`} />
              {cartCount > 0 && (
                <span className={`absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-cyan-600 text-white text-[10px] sm:text-xs rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center font-bold ${cartPulse ? 'animate-ping-once' : ''}`}>
                  {cartCount}
                </span>
              )}
            </Button>

            <button
              className="md:hidden text-white p-1 -mr-1"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900 border-t border-slate-800">
          <nav className="px-4 py-4 space-y-1">
            <button
              onClick={() => scrollToSection('productos')}
              className="block w-full text-left text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium py-2.5"
            >
              {t(language, 'nav.products')}
            </button>

            <button
              onClick={() => scrollToSection('certificacion')}
              className="block w-full text-left text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium py-2.5"
            >
              {tx(language, 'Certificación', 'Certification', 'כשרות')}
            </button>
            <button
              onClick={() => scrollToSection('contacto')}
              className="block w-full text-left text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium py-2.5"
              data-testid="nav-contact-mobile"
            >
              {tx(language, 'Contacto', 'Contact', 'צור קשר')}
            </button>

            {/* Mobile sub-menu */}
            <button
              onClick={() => setMobileSubOpen(!mobileSubOpen)}
              className="flex items-center justify-between w-full text-left text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium py-2.5"
            >
              {tx(language, 'Más del Tzitzit', 'More about Tzitzit', 'עוד על הציצית')}
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileSubOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileSubOpen && (
              <div className="pl-4 space-y-1 border-l-2 border-cyan-500/30 ml-2">
                {subLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="block w-full text-left text-slate-400 hover:text-cyan-400 transition-colors duration-200 text-sm py-2"
                    data-testid={`nav-mobile-sub-${link.id}`}
                  >
                    {tx(language, link.es, link.en, link.he)}
                  </button>
                ))}
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
